import React, { useState } from "react";
import { usePatientOrder } from "../data/patientOrder";
import EwaySecureFields from "./EwaySecureFields";
import AddressFields from "./AddressFields";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { useHistory } from "react-router-dom";
import { gtmTriggerEvent } from "../data/gtm";
import { InputField } from "../components/input";
import Popup from "./Popup";

const Spinner = () => {
  return (
    <svg
      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
};

export default function CheckoutForm({ token, orderingBlocked = false }) {
  const { patientOrder, isLoading } = usePatientOrder(token);
  // Ticket 25660 — a mixture containing a dispensary-flagged herb can't be ordered.
  // The server rejects it; here we disable Pay and explain why up front.
  const hasUnavailableItem = Boolean(
    patientOrder?.line_items?.some((li) => li.temporarily_unavailable)
  );
  const [authorityToLeave, setAuthorityToLeave] = useState(false);
  const [isBillingSameAsShipping, setIsBillingSameAsShipping] = useState(true);
  const [secureFieldCode, setSecureFieldCode] = useState(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const history = useHistory();

  const [validationErrors, setValidationErrors] = useState({
    shippingAddress: {
      address: false,
      suburb: false,
      city: false,
      business: false,
      postalCode: false,
    },
    billingAddress: {},
  });

  const [shippingAddress, setShippingAddress] = useState({
    address: "",
    suburb: "",
    city: "",
    business: "",
    postalCode: "",
  });

  const [billingAddress, setBillingAddress] = useState({
    suburb: "",
    city: "",
    business: "",
    postalCode: "",
  });

  const [cardErrors, setCardErrors] = useState({
    cardHolderName: false,
    cardCVN: false,
    cardNumber: false,
    cardExpiry: false,
  });

  const validateForm = () => {
    const newErrors = {
      shippingAddress: {
        address: !shippingAddress.address,
        city: !shippingAddress.city,
        postalCode: !shippingAddress.postalCode,
      },
      billingAddress: {},
    };

    setValidationErrors(newErrors);
    console.log(validationErrors);

    return (
      Object.values(newErrors.shippingAddress).some(Boolean) ||
      Object.values(newErrors.billingAddress).some(Boolean)
    );
  };

  const handleCardErrors = (errors) => {
    const containsError = (codes) =>
      errors.some((error) => codes.includes(error));

    setCardErrors({
      cardName: containsError(["V6021", "V6100"]),
      cardCVN: containsError(["V6023", "V6106"]),
      cardNumber: containsError(["V6110", "V6022", "V6010"]),
      cardExpiry: containsError(["V6033", "V6101", "V6102"]),
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    setError(false);

    const hasErrors = validateForm();
    if (hasErrors) {
      setError(true);
      setPopupMessage(
        "There is an issue with your address. Ensure you select an address from the dropdown and try again."
      );
      setPopupOpen(true);
      setIsProcessing(false);
      return;
    }

    const requestBody = {
      secureFieldCode: secureFieldCode,
      token: token,
      shippingAddress: shippingAddress,
      billingAddress: billingAddress,
      authorityToLeave: authorityToLeave,
    };

    try {
      const response = await fetch("/api/orders/create-eway-transaction", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (!response.ok) {
        setError(true);
        setPopupMessage("Please check your card details and try again.");
        setPopupOpen(true);
        console.log(responseData);
        if (responseData.ordering_paused_error) {
          // The pause began between page load and submit.
          setPopupMessage(responseData.ordering_paused_error);
        } else if (responseData.unavailable_herb_error) {
          setPopupMessage(responseData.unavailable_herb_error);
        } else if (responseData.eway_errors) {
          handleCardErrors(responseData.eway_errors);
        } else if (responseData.response_code) {
          handleCardErrors([responseData.response_code]);
        } else {
          // Handle other types of errors
        }
      } else {
        setPaymentSuccess(true);
        setPopupMessage("Thanks for your purchase!");
        setPopupOpen(true);
        gtmTriggerPurchase(responseData.transaction_id);
        setTimeout(
          () => history.replace(`/transaction/${responseData.uuid}`),
          1000
        );
      }
    } catch (error) {
      setError(true);
      setPopupMessage("Your payment could not be processed. Please try again.");
      setPopupOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const gtmTriggerPurchase = (transaction_id) => {
    const shipping = parseFloat(patientOrder?.shipping.rate);
    const sub_total = parseFloat(patientOrder?.sub_total);
    const tax = parseFloat(patientOrder?.gst);

    const items = patientOrder.line_items.map((lineItem) => ({
      item_name: lineItem.name,
      price: lineItem.patient_price,
      quantity: lineItem.ordering_quantity,
    }));

    gtmTriggerEvent("purchase", {
      currency: "NZD",
      value: sub_total,
      shipping: shipping,
      tax: tax,
      transaction_id: transaction_id,
      items: items,
    });
  };

  return (
    <section className="flex-auto pb-16 pt-12 sm:pt-16 lg:pb-24 lg:pt-0">
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-12 gap-x-4 gap-y-4">
          <InputField
            disabled
            label="Email Address"
            type="email"
            id="email-address"
            name="email-address"
            autoComplete="email"
            value={patientOrder?.patient.email}
            isLoading={!patientOrder?.patient.email}
          />

          <AddressFields
            label="Shipping"
            setAddress={setShippingAddress}
            address={shippingAddress}
            errors={validationErrors.shippingAddress}
            setErrors={(newErrors) =>
              setValidationErrors((prev) => ({
                ...prev,
                shippingAddress: newErrors,
              }))
            }
          />

          {!isBillingSameAsShipping && (
            <AddressFields
              label="Billing"
              setAddress={setBillingAddress}
              address={billingAddress}
              errors={validationErrors.billingAddress}
              setErrors={(newErrors) =>
                setValidationErrors((prev) => ({
                  ...prev,
                  billingAddress: newErrors,
                }))
              }
            />
          )}

          <div
            className={`col-span-full flex space-x-2 ${isLoading ? "invisible" : ""}`}
          >
            <CheckboxField
              id="same-as-shipping"
              label="Billing address same as shipping"
              checked={isBillingSameAsShipping}
              onChange={() =>
                setIsBillingSameAsShipping(!isBillingSameAsShipping)
              }
            />
          </div>

          <div
            className={`flex col-span-full space-x-2 mb-3 ${isLoading ? "invisible" : ""}`}
          >
            <CheckboxField
              id="authority-to-leave"
              label="Authority to leave parcel unattended at door"
              checked={authorityToLeave}
              onChange={() => setAuthorityToLeave(!authorityToLeave)}
            />
          </div>

          <EwaySecureFields
            setSecureFieldCode={setSecureFieldCode}
            cardErrors={cardErrors}
            company_code={patientOrder?.practitioner.company_code}
          />
        </div>

        {/* No "View notice" link: the block modal cannot be dismissed, so it is
            always covering this line and there is nothing to go back to. */}
        {orderingBlocked && (
          <p className="mt-6 text-sm text-gray-700">
            Ordering is temporarily unavailable. Please contact your practitioner
            if you have any questions.
          </p>
        )}

        {hasUnavailableItem && (
          <p className="mt-6 text-sm text-red-600">
            This order contains a herb that is temporarily unavailable. Please ask
            your practitioner to substitute it before ordering.
          </p>
        )}

        <button
          type="submit"
          disabled={
            isProcessing ||
            isLoading ||
            paymentSuccess ||
            !patientOrder?.total ||
            orderingBlocked ||
            hasUnavailableItem
          }
          className={`flex justify-center mt-6 w-full rounded-md border border-transparent bg-integria-green px-4 py-2 text-sm font-medium text-white hover:bg-integria-green/90 focus:outline-hidden focus:border-blue-400 disabled:opacity-80 disabled:pointer-events-none disabled:cursor-not-allowed`}
        >
          {paymentSuccess ? (
            <>
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              &nbsp;Success
            </>
          ) : isProcessing ? (
            <>
              <Spinner />
              &nbsp;Processing
            </>
          ) : isLoading ? (
            <>&nbsp;</>
          ) : patientOrder?.total > 0 ? (
            `Pay $${parseFloat(patientOrder?.total).toFixed(2)}`
          ) : (
            "Pay"
          )}
        </button>
      </form>
      <Popup
        open={popupOpen}
        setOpen={setPopupOpen}
        success={paymentSuccess}
        message={popupMessage}
      />
    </section>
  );
}

const CheckboxField = ({ id, label, checked, onChange }) => (
  <>
    <div className="flex h-5 items-center">
      <input
        id={id}
        name={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded-sm border-gray-300 text-gray-600 shadow-xs focus:ring-0"
      />
    </div>
    <label htmlFor={id} className="text-sm font-medium text-gray-700">
      {label}
    </label>
  </>
);
